import axios from 'axios';

const API_BASE_URL = __VITE_API_URL__ || 'https://api.cleanurphone.xyz/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add language header
    const language = localStorage.getItem('language') || 'en';
    config.headers['Accept-Language'] = language;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Return data directly for easier access
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      console.log('🔒 API: 401 Unauthorized - Removing token');
      localStorage.removeItem('auth_token');
      // Don't redirect here - let React Router handle it
      // window.location.href = '/login'; // REMOVED - causes infinite loop
    }
    
    // Return error in consistent format
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject({
      success: false,
      message: errorMessage,
      status: error.response?.status
    });
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  verifyEmail: (code) => api.post('/auth/verify-email', { code }),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  testEmail: () => api.get('/auth/test-email'),
};

// User API
export const userAPI = {
  getProfile: (id) => api.get(`/users/${id}`),
  getUserGifts: (id) => api.get(`/users/${id}/gifts`),
  getUserFriends: (id) => api.get(`/users/${id}/friends`),
  getVisitors: (id) => api.get(`/users/${id}/visitors`),
  updateProfile: (data) => api.put('/users/profile', data),
  uploadPhoto: (formData) => api.post('/users/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deletePhoto: (photoId) => api.delete(`/users/photo/${photoId}`),
  getPhotos: (userId) => api.get(`/users/${userId}/photos`),
  requestBulkPhotoAccess: (photoIds) => api.post('/users/photos/request-access-bulk', { photoIds }),
  requestPhotoAccess: (photoId) => api.post(`/users/photo/${photoId}/request-access`),
  getTotalUserCount: () => api.get('/users/count'),
  respondToPhotoAccess: (requestId, action) => api.put(`/users/photo-access/${requestId}`, { action }),
  getPhotoAccessRequests: () => api.get('/users/photo-access-requests'),
  getApprovedPhotoAccess: () => api.get('/users/photo-access-approved'),
  updatePhotoType: (photoId, photoType) => api.put(`/users/photo/${photoId}/type`, { photo_type: photoType }),
  sendFriendRequest: (userId) => api.post('/users/friend-request', { friend_id: userId }),
  removeFriend: (userId) => api.delete(`/users/friend/${userId}`),
  getTotalUserCount: () => api.get('/users/count'),
};

// People API
export const peopleAPI = {
  getPeople: (page = 1, filters = {}) => api.get('/people', { params: { page, ...filters } }),
  getRandomPeople: (limit = 10) => api.get('/people/random', { params: { limit } }),
  getNearbyPeople: (latitude, longitude, radius = 50, page = 1, limit = 20) =>
    api.get('/people/nearby', { params: { latitude, longitude, radius, page, limit } }),
};

// Encounters API
export const encountersAPI = {
  getEncounter: () => api.get('/encounters/next'),
  likeProfile: (profileId) => api.post('/encounters/like', { profile_id: profileId }),
  dislikeProfile: (profileId) => api.post('/encounters/dislike', { profile_id: profileId }),
  getMatches: (page = 1, limit = 20) => api.get('/encounters/matches', { params: { page, limit } }),
};

// Messages API
export const messagesAPI = {
  getChats: async () => {
    try {
      return await api.get('/messages/chats');
    } catch (error) {
      // Offline mode - return mock data
      if (!navigator.onLine || error.code === 'NETWORK_ERROR') {
        console.log('📴 Offline mode: Returning mock chats data');
        return {
          data: {
            success: true,
            chats: [
              {
                id: 1,
                full_name: 'Ahmet Kaya',
                profile_picture: null,
                last_active: new Date().toISOString(),
                age: 28,
                city: 'Istanbul',
                country: 'Turkey',
                last_message: 'Merhaba! Nasılsın?',
                last_message_time: new Date().toISOString(),
                unread_count: 2,
                isOnline: true
              },
              {
                id: 2,
                full_name: 'Elif Demir',
                profile_picture: null,
                last_active: new Date(Date.now() - 3600000).toISOString(),
                age: 32,
                city: 'Ankara',
                country: 'Turkey',
                last_message: 'Teşekkürler! 😊',
                last_message_time: new Date(Date.now() - 3600000).toISOString(),
                unread_count: 0,
                isOnline: false
              }
            ]
          }
        };
      }
      throw error;
    }
  },
  getChat: async (userId) => {
    try {
      return await api.get(`/messages/chat/${userId}`);
    } catch (error) {
      // Offline mode - return mock data
      if (!navigator.onLine || error.code === 'NETWORK_ERROR') {
        console.log('📴 Offline mode: Returning mock chat data');
        return {
          data: {
            success: true,
            user: {
              id: parseInt(userId),
              full_name: 'Demo User',
              profile_picture: null,
              last_active: new Date().toISOString(),
              isOnline: true
            },
            messages: [
              {
                id: 1,
                message: 'Merhaba! Nasılsın?',
                time: Date.now() / 1000,
                is_read: 1,
                sender: {
                  id: parseInt(userId),
                  full_name: 'Demo User',
                  profile_picture: null
                }
              },
              {
                id: 2,
                message: 'İyiyim teşekkürler! Sen nasılsın?',
                time: Date.now() / 1000 + 60,
                is_read: 1,
                sender: {
                  id: 999, // Current user
                  full_name: 'You',
                  profile_picture: null
                }
              },
              {
                id: 3,
                message: '',
                time: Date.now() / 1000 + 120,
                is_read: 1,
                is_gift: 1,
                gift_id: 1,
                gift_name: 'Rose',
                sender: {
                  id: parseInt(userId),
                  full_name: 'Demo User',
                  profile_picture: null
                }
              },
              {
                id: 4,
                message: '',
                time: Date.now() / 1000 + 180,
                is_read: 1,
                is_photo_request: 1,
                photo_request_id: 1,
                sender: {
                  id: 999, // Current user
                  full_name: 'You',
                  profile_picture: null
                }
              },
              {
                id: 5,
                message: '',
                time: Date.now() / 1000 + 240,
                is_read: 1,
                is_friend_request: 1,
                friend_request_id: 1,
                sender: {
                  id: parseInt(userId),
                  full_name: 'Demo User',
                  profile_picture: null
                }
              }
            ],
            lastMessageId: 3
          }
        };
      }
      throw error;
    }
  },
  getNewMessages: (userId, lastMessageId) => api.get(`/messages/chat/${userId}/new?lastMessageId=${lastMessageId || 0}`),
  getNewMessages: (userId, lastMessageId) => api.get(`/messages/chat/${userId}/new?lastMessageId=${lastMessageId || 0}`),
  sendMessage: (data) => api.post('/messages/send', data),
  deleteMessages: (chatId) => api.delete(`/messages/${chatId}`),
  getStickerPacks: () => api.get('/messages/sticker-packs'),
  getStickers: (packId) => api.get(`/messages/stickers/${packId}`),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: () => api.get('/notifications'),
  getNotificationCount: () => api.get('/notifications/count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Likes API
export const likesAPI = {
  getLikes: () => api.get('/likes'),
  likeProfile: (profileId) => api.post('/likes/like', { profile_id: profileId }),
  unlikeProfile: (profileId) => api.delete('/likes/like', { data: { profile_id: profileId } }),
  getVisitors: (limit = 20, offset = 0) => api.get('/likes/visitors', { params: { limit, offset } }),
};

// Friends API
export const friendsAPI = {
  sendRequest: (userId) => api.post('/friends/request', { user_id: userId }),
  acceptRequest: (userId) => api.post('/friends/accept', { user_id: userId }),
  cancelRequest: (userId) => api.delete(`/friends/request/${userId}`),
  unfriend: (userId) => api.delete(`/friends/${userId}`),
  getFriends: (userId) => api.get(`/friends/${userId}`),
  getRequests: () => api.get('/friends/requests'),
};

// Gifts API
export const giftsAPI = {
  sendGift: (data) => api.post('/gifts/send', data),
  getGifts: (userId) => api.get(`/gifts/${userId}`),
  getAvailableGifts: () => api.get('/gifts/available'),
  getCatalog: () => api.get('/gifts/catalog'),
  getStickerPacks: () => api.get('/gifts/sticker-packs'),
};

// VIP API - with fallback for offline mode
export const vipAPI = {
  getPricing: () => {
    try {
      return api.get('/vip/pricing');
    } catch (error) {
      console.warn('Backend not available, using offline VIP pricing mode');
      return Promise.resolve({
        data: {
          success: true,
          pricing: [
            { id: 1, duration: 30, price: 5.00, currency: 'TRY' },
            { id: 2, duration: 90, price: 15.00, currency: 'TRY' },
            { id: 3, duration: 180, price: 30.00, currency: 'TRY' }
          ]
        }
      });
    }
  },
  subscribe: (data) => api.post('/vip/subscribe', data),
  getStatus: () => api.get('/vip/status'),
  cancel: () => api.post('/vip/cancel'),
};

// Credits API - with fallback for offline mode
export const creditsAPI = {
  getPricing: () => {
    try {
      return api.get('/credits/pricing');
    } catch (error) {
      console.warn('Backend not available, using offline credits pricing mode');
      return Promise.resolve({
        data: {
          success: true,
          pricing: [
            { id: 1, credits: 100, price: 1.00, currency: 'TRY' },
            { id: 2, credits: 500, price: 5.00, currency: 'TRY' },
            { id: 3, credits: 1000, price: 10.00, currency: 'TRY' },
            { id: 4, credits: 1500, price: 15.00, currency: 'USD' }
          ]
        }
      });
    }
  },
  purchase: (data) => api.post('/credits/purchase', data),
  purchaseStickerPack: (data) => api.post('/credits/purchase-sticker-pack', data),
  getBalance: () => api.get('/credits/balance'),
  getTransactions: () => api.get('/credits/transactions'),
};

// Spotlight API
export const spotlightAPI = {
  getSpotlight: () => api.get('/spotlight'),
  activate: (data) => api.post('/spotlight/activate', data),
};

// Settings API
export const settingsAPI = {
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
  updatePassword: (data) => api.put('/settings/password', data),
  updatePreferences: (data) => api.put('/settings/preferences', data),
  deleteAccount: (password) => api.delete('/settings/account', { data: { password } }),
};

// Ads API
export const adsAPI = {
  getAdsByLocation: (location) => api.get(`/ads/${location}`),
  getAdTypes: () => api.get('/ads/types'),
  getAdLocations: () => api.get('/ads/locations'),
  getAllAds: () => api.get('/ads'),
  createAd: (data) => api.post('/ads', data),
  updateAd: (id, data) => api.put(`/ads/${id}`, data),
  deleteAd: (id) => api.delete(`/ads/${id}`),
};

// Reports API
export const reportsAPI = {
  submitReport: (data) => api.post('/reports/submit', data),
  getUserReports: () => api.get('/reports/my-reports'),
  blockUser: (data) => api.post('/reports/block', data),
  unblockUser: (data) => api.post('/reports/unblock', data),
  checkBlockStatus: (userId) => api.get(`/reports/block-status/${userId}`),
};

// Language API
export const languageAPI = {
  getLanguages: () => api.get('/languages'),
};

// Permissions API - with fallback for offline mode
export const permissionsAPI = {
  getUserPermissions: () => {
    try {
      return api.get('/permissions');
    } catch (error) {
      console.warn('Backend not available, using offline mode');
      return Promise.resolve({
        data: {
          success: true,
          permissions: {
            location: { granted: false, granted_at: null, revoked_at: null },
            camera: { granted: false, granted_at: null, revoked_at: null },
            microphone: { granted: false, granted_at: null, revoked_at: null },
            notifications: { granted: false, granted_at: null, revoked_at: null },
          }
        }
      });
    }
  },
  updatePermission: async (permissionType, granted) => {
    try {
      return await api.post('/permissions/update', { permission_type: permissionType, granted });
    } catch (error) {
      console.warn('Backend not available, permission update ignored');
      return { data: { success: true } };
    }
  },
  requestPermission: async (permissionType) => {
    try {
      return await api.post('/permissions/request', { permission_type: permissionType });
    } catch (error) {
      console.warn('Backend not available, permission request ignored');
      return { data: { success: true } };
    }
  },
  getPermissionStatus: async (permissionType) => {
    try {
      return await api.get(`/permissions/status/${permissionType}`);
    } catch (error) {
      console.warn('Backend not available, using default permission status');
      return {
        data: {
          success: true,
          permission: {
            type: permissionType,
            granted: false,
            granted_at: null,
            revoked_at: null
          }
        }
      };
    }
  },
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  testDatabase: () => api.get('/admin/test-db'),

  // Users management
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  banUser: (userId, data) => api.post(`/admin/users/${userId}/ban`, data),
  unbanUser: (userId) => api.post(`/admin/users/${userId}/unban`),

  // Roles management
  getRoles: () => api.get('/admin/roles'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRole: (roleId, data) => api.put(`/admin/roles/${roleId}`, data),
  deleteRole: (roleId) => api.delete(`/admin/roles/${roleId}`),
  getUserRoles: (userId) => api.get(`/admin/users/${userId}/roles`),
  assignUserRole: (userId, data) => api.post(`/admin/users/${userId}/roles`, data),
  bulkAssignUserRoles: (userId, data) => api.post(`/admin/users/${userId}/roles/bulk`, data),
  updateUserRole: (userId, roleId, data) => api.put(`/admin/users/${userId}/roles/${roleId}`, data),
  removeUserRole: (userId, roleId) => api.delete(`/admin/users/${userId}/roles/${roleId}`),

  // Reports management
  getReports: (params = {}) => api.get('/admin/reports', { params }),
  getReportDetails: (reportId) => api.get(`/admin/reports/${reportId}`),
  updateReportStatus: (reportId, data) => api.put(`/admin/reports/${reportId}`, data),

  // Finance management
  getTransactions: (params = {}) => api.get('/admin/finance/transactions', { params }),
  getFinanceStats: (params = {}) => api.get('/admin/finance/stats', { params }),
  refundTransaction: (transactionId, data) => api.post(`/admin/finance/transactions/${transactionId}/refund`, data),

  // Ads management
  getAds: (params = {}) => api.get('/admin/ads', { params }),
  getAdsStats: () => api.get('/admin/ads/stats'),
  createAd: (data) => api.post('/admin/ads', data),
  updateAd: (adId, data) => api.put(`/admin/ads/${adId}`, data),
  deleteAd: (adId) => api.delete(`/admin/ads/${adId}`),
  toggleAdStatus: (adId) => api.patch(`/admin/ads/${adId}/toggle`)
};

// Pages API - with fallback for offline mode
export const pagesAPI = {
  getPages: async () => {
    try {
      return await api.get('/pages');
    } catch (error) {
      console.warn('Backend not available, using offline pages mode');
      return {
        data: {
          success: true,
          pages: [
            { id: 1, title: 'Terms of Service', content: 'Terms of service content' },
            { id: 2, title: 'Privacy Policy', content: 'Privacy policy content' },
            { id: 3, title: 'Support', content: 'Support content' },
            { id: 4, title: 'About Us', content: 'About us content' },
            { id: 5, title: 'Safety Tips', content: 'Safety tips content' }
          ]
        }
      };
    }
  },
  getPage: async (pageTitle) => {
    try {
      return await api.get(`/pages/${encodeURIComponent(pageTitle)}`);
    } catch (error) {
      console.warn('Backend not available, using offline page mode');
      const offlinePages = {
        'Terms of Service': {
          id: 1,
          title: 'Terms of Service',
          content: '<h2>Terms of Service</h2><p>Terms of service content will be available when the server is online.</p>'
        },
        'Privacy Policy': {
          id: 2,
          title: 'Privacy Policy',
          content: '<h2>Privacy Policy</h2><p>Privacy policy content will be available when the server is online.</p>'
        },
        'Support': {
          id: 3,
          title: 'Support',
          content: '<h2>Help & Support</h2><p>Support information will be available when the server is online.</p>'
        },
        'About Us': {
          id: 4,
          title: 'About Us',
          content: '<h2>About MatchUs</h2><p>Connecting hearts worldwide with safe, meaningful relationships.</p>'
        },
        'Safety Tips': {
          id: 5,
          title: 'Safety Tips',
          content: '<h2>Safety Tips</h2><p>Your safety is our priority. Safety information will be available when the server is online.</p>'
        }
      };

      return {
        data: {
          success: true,
          page: offlinePages[pageTitle] || {
            id: 0,
            title: pageTitle,
            content: '<p>Page content will be available when the server is online.</p>'
          }
        }
      };
    }
  },
};

export default api;

